import { configureStore, createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { getEvents, getMembers, getRawOutlookEvents } from "./api";
import { moveMemberId, settingsFromHash, settingsHash, sanitizeSettings } from "./settings";
import type { LoadStatus, Member, ScheduleEvent, Settings } from "./types";

const initialSettings = settingsFromHash(window.location.hash);

const settingsSlice = createSlice({
  name: "settings",
  initialState: initialSettings,
  reducers: {
    setPeriod(state, action: PayloadAction<Partial<Pick<Settings, "from" | "to">>>) {
      if (action.payload.from) state.from = action.payload.from;
      if (action.payload.to) state.to = action.payload.to;
    },
    addMember(state, action: PayloadAction<string>) {
      if (!state.memberIds.includes(action.payload)) state.memberIds.push(action.payload);
    },
    removeMember(state, action: PayloadAction<string>) {
      state.memberIds = state.memberIds.filter((id) => id !== action.payload);
    },
    moveMember(state, action: PayloadAction<{ memberId: string; toIndex: number }>) {
      state.memberIds = moveMemberId(state.memberIds, action.payload.memberId, action.payload.toIndex);
    },
    /** ドラッグ中止時に、ドラッグ開始前の並びへ戻すために使う。 */
    setMemberOrder(state, action: PayloadAction<string[]>) {
      state.memberIds = [...action.payload];
    },
    setMinDuration(state, action: PayloadAction<number>) {
      state.minDurationHours = action.payload;
    },
    setTeamAddressVisible(
      state,
      action: PayloadAction<{ address: string; visible: boolean }>,
    ) {
      const address = action.payload.address.trim().toLowerCase();
      state.hiddenTeamAddresses = action.payload.visible
        ? state.hiddenTeamAddresses.filter((value) => value !== address)
        : [...new Set([...state.hiddenTeamAddresses, address])];
    },
    setBoolean(
      state,
      action: PayloadAction<{
        key:
          | "rainbowEnabled"
          | "mergeEnabled"
          | "showWeekends"
          | "showUnassignedEvents"
          | "showRawOutlookEvents";
        value: boolean;
      }>,
    ) {
      state[action.payload.key] = action.payload.value;
    },
    loadSettings(_state, action: PayloadAction<unknown>) {
      return sanitizeSettings(action.payload);
    },
  },
});

interface DataState {
  members: Member[];
  events: ScheduleEvent[];
  rawOutlookEvents: ScheduleEvent[];
  membersStatus: LoadStatus;
  eventsStatus: LoadStatus;
  rawOutlookStatus: LoadStatus;
  membersError: string | null;
  eventsError: string | null;
  rawOutlookError: string | null;
}

const initialData: DataState = {
  members: [],
  events: [],
  rawOutlookEvents: [],
  membersStatus: "idle",
  eventsStatus: "idle",
  rawOutlookStatus: "idle",
  membersError: null,
  eventsError: null,
  rawOutlookError: null,
};

export const fetchMembers = createAsyncThunk("data/fetchMembers", async (_: void, { signal }) => getMembers(signal));
export const fetchEvents = createAsyncThunk(
  "data/fetchEvents",
  async ({ from, to }: Pick<Settings, "from" | "to">, { signal }) => getEvents(from, to, signal),
);

export const fetchRawOutlookEvents = createAsyncThunk(
  "data/fetchRawOutlookEvents",
  async (
    { from, to, memberIds }: Pick<Settings, "from" | "to" | "memberIds">,
    { signal },
  ) => getRawOutlookEvents(from, to, memberIds, signal),
);

const errorMessage = (error: { message?: string }): string => error.message || "データを取得できませんでした";

const dataSlice = createSlice({
  name: "data",
  initialState: initialData,
  reducers: {
    updateEventParticipants(
      state,
      action: PayloadAction<{ eventId: string; memberIds: string[] }>,
    ) {
      const event = state.events.find((item) => item.id === action.payload.eventId);
      if (event) event.memberIds = action.payload.memberIds;
    },
    removeEvent(state, action: PayloadAction<string>) {
      state.events = state.events.filter((event) => event.id !== action.payload);
    },
    clearRawOutlookEvents(state) {
      state.rawOutlookEvents = [];
      state.rawOutlookStatus = "idle";
      state.rawOutlookError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchMembers.pending, (state) => {
        state.membersStatus = "loading";
        state.membersError = null;
      })
      .addCase(fetchMembers.fulfilled, (state, action) => {
        state.membersStatus = "succeeded";
        state.members = action.payload;
      })
      .addCase(fetchMembers.rejected, (state, action) => {
        state.membersStatus = action.meta.aborted ? "idle" : "failed";
        state.membersError = action.meta.aborted ? null : errorMessage(action.error);
      })
      .addCase(fetchEvents.pending, (state) => {
        state.eventsStatus = "loading";
        state.eventsError = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.eventsStatus = "succeeded";
        state.events = action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.eventsStatus = action.meta.aborted ? "idle" : "failed";
        state.eventsError = action.meta.aborted ? null : errorMessage(action.error);
      })
      .addCase(fetchRawOutlookEvents.pending, (state) => {
        state.rawOutlookStatus = "loading";
        state.rawOutlookError = null;
      })
      .addCase(fetchRawOutlookEvents.fulfilled, (state, action) => {
        state.rawOutlookStatus = "succeeded";
        state.rawOutlookEvents = action.payload;
      })
      .addCase(fetchRawOutlookEvents.rejected, (state, action) => {
        state.rawOutlookStatus = action.meta.aborted ? "idle" : "failed";
        state.rawOutlookError = action.meta.aborted ? null : errorMessage(action.error);
      });
  },
});

export const settingsActions = settingsSlice.actions;
export const dataActions = dataSlice.actions;

export const store = configureStore({
  reducer: {
    settings: settingsSlice.reducer,
    data: dataSlice.reducer,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

let lastSettingsHash = "";
store.subscribe(() => {
  const hash = settingsHash(store.getState().settings);
  if (hash === lastSettingsHash) return;
  lastSettingsHash = hash;
  window.history.replaceState(null, "", hash);
});
